/**
 * 地图编辑器顶部搜索/筛选工具栏
 *
 * 职责：
 * 1. 提供位置、城市站点、停靠点、车号、经纬度等多种搜索类型的 UI 入口
 * 2. 管理地图叠加图层（动态地图、车道边线、车道中心线等）的显隐开关
 * 3. 控制"新增动态地图" / "取消绘制" 按钮的状态与回调
 *
 * 该组件为纯展示组件，所有业务回调通过 props 注入，保持与地图逻辑解耦。
 */
import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Select, Cascader, Button, Checkbox, Input, Form, Spin } from 'antd';
import {
  CLSTAG,
  LayerIdEnum,
  PermissionButton,
  PositionSelectType,
} from '@/utils/constant';
import { debounce, getPermissionById } from '@/utils';
import { selectOptions, SearchFormType } from '../utils/config';
import { clstagStr } from '../utils';

interface Props {
  searchForm: Partial<SearchFormType> & Record<string, any>;
  showEditForm: boolean;
  isLocationDropdownOpen: boolean;
  /** 地图瓦片是否已加载完成；未完成时禁用工具栏交互（micro-app 宿主中 BMapGL 初始化期间下拉弹层挂载失败，见 AGENTS.md 坑17） */
  isMapReady: boolean;
  isDrawing: boolean;
  /** 是否处于路径规划模式（进入后禁用"新增动态地图"，入口按钮文案切换） */
  planningMode: boolean;
  locationOptionList: Array<{ value: string; label: string }>;
  stopOptionList: Array<{ value: string; label: string }>;
  vehicleOptionList: Array<{ value: string; label: string }>;
  cityList: any;
  unSelectedLayer: any[];
  ldMapVisible: boolean;
  toggleLdMap: () => void;
  searchCallBack: (params: Partial<SearchFormType>) => void;
  handleLocationSearch: (keyword: string) => void;
  confirmSearch: (params: Partial<SearchFormType>) => void;
  handleSelectStop: AnyFunc;
  handleSelectlatlng: AnyFunc;
  changeCascader: (value: any, selectedOptions: any) => void;
  changeLayerVisible: AnyFunc;
  cancelPaint: AnyFunc;
  onClickDraw: AnyFunc;
  onToggleRoutePlan: AnyFunc;
  handleFocus: AnyFunc;
  handleBlur: AnyFunc;
}

/** 图层显隐复选框静态配置：value 对应图层 ID，checked 状态由 unSelectedLayer 取反得到 */
const LAYER_CHECKBOX_CONFIG = [
  {
    value: LayerIdEnum.ANCHOR_LAYER,
    label: '动态地图',
    clstag: clstagStr(CLSTAG[LayerIdEnum.ANCHOR_LAYER]),
  },
  {
    value: LayerIdEnum.BOUNDARY_LAYER,
    label: '车道边线',
    clstag: clstagStr(CLSTAG[LayerIdEnum.BOUNDARY_LAYER]),
  },
  {
    value: LayerIdEnum.WSM_LAYER,
    label: '车道中心线',
    clstag: clstagStr(CLSTAG[LayerIdEnum.WSM_LAYER]),
  },
  {
    value: LayerIdEnum.INTER_LAYER,
    label: '路口',
    clstag: clstagStr(CLSTAG[LayerIdEnum.INTER_LAYER]),
  },
  {
    value: LayerIdEnum.OPENAREA_LAYER,
    label: '开阔区域',
    clstag: clstagStr(CLSTAG[LayerIdEnum.OPENAREA_LAYER]),
  },
  {
    value: LayerIdEnum.GATE_LAYER,
    label: '大门',
    clstag: clstagStr(CLSTAG[LayerIdEnum.GATE_LAYER]),
  },
  {
    value: LayerIdEnum.STOPLINE_LAYER,
    label: '停止线',
    clstag: clstagStr(CLSTAG[LayerIdEnum.STOPLINE_LAYER]),
  },
];

/**
 * 下拉面板挂载到触发节点父级：避免宿主开启样式隔离时，body 挂载的弹层丢失子应用样式。
 * 注意：本地挂载在 micro-app 加载窗口期存在静默失败风险（AGENTS.md 坑17），
 * 该风险由 isMapReady 门控兜底（地图就绪前工具栏禁用交互），此处可安全使用。
 */
const getLocalPopupContainer = (triggerNode: HTMLElement) =>
  triggerNode.parentElement ?? triggerNode;

const SearchTools = (props: Props) => {
  const {
    searchForm,
    showEditForm,
    isDrawing,
    cityList,
    locationOptionList,
    stopOptionList,
    vehicleOptionList,
    unSelectedLayer,
    ldMapVisible,
    toggleLdMap,
    isLocationDropdownOpen,
    isMapReady,
    planningMode,
    searchCallBack,
    confirmSearch,
    changeCascader,
    handleSelectStop,
    handleSelectlatlng,
    changeLayerVisible,
    cancelPaint,
    onClickDraw,
    onToggleRoutePlan,
    handleLocationSearch,
    handleBlur,
    handleFocus,
  } = props;

  /** 地图未就绪或编辑/绘制中，工具栏交互控件统一禁用 */
  const controlsDisabled = !isMapReady || showEditForm || isDrawing;

  const locationSelectRef = useRef<any>(null);
  const vehicleSelectRef = useRef<any>(null);
  // 位置搜索防抖定时器引用，组件卸载时清理避免内存泄漏
  const searchTimerRef = useRef<number | null>(null);

  /** 清理位置搜索防抖定时器 */
  const clearSearchTimer = useCallback(() => {
    if (searchTimerRef.current !== null) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearSearchTimer();
    };
  }, [clearSearchTimer]);

  /** 切换搜索类型（位置/站点/停靠点/车号/经纬度） */
  const handleChange = useCallback(
    (value: string) => {
      searchCallBack({
        searchType: value,
      });
    },
    [searchCallBack]
  );

  /**
   * 位置搜索输入处理（带 200ms 防抖）
   * 清空输入时重置位置搜索条件；有值时触发地点检索
   */
  const handleSearch = useCallback(
    (value: string) => {
      clearSearchTimer();
      const searchText = value?.trim();

      if (!searchText) {
        searchCallBack({
          searchType: PositionSelectType.position,
          value: null,
        });
        return;
      }

      searchTimerRef.current = window.setTimeout(() => {
        handleLocationSearch(searchText);
        searchTimerRef.current = null;
      }, 200);
    },
    [clearSearchTimer, handleLocationSearch, searchCallBack]
  );

  /** 选中位置下拉项：触发确认搜索并收起下拉 */
  const hanleOnSelect = useCallback(
    (value: string) => {
      const selectedOption = locationOptionList.find(
        (option) => option.value === value
      );
      confirmSearch({
        searchType: PositionSelectType.position,
        value,
        locationKeyword: selectedOption?.label ?? searchForm.value,
      } as Partial<SearchFormType>);
      locationSelectRef.current?.blur?.();
    },
    [confirmSearch, locationOptionList, searchForm.value]
  );

  /** 点击图层显隐复选框：切换对应图层可见性 */
  const handleCheckbox = useCallback(
    (item: any) => {
      changeLayerVisible && changeLayerVisible(item.value);
    },
    [changeLayerVisible]
  );

  /** 位置搜索下拉选项，避免每次渲染生成新数组引用导致 Select 重渲染 */
  const locationSelectOptions = useMemo(
    () =>
      locationOptionList.map((option) => ({
        ...option,
        label: (
          <div className="custom-option-label" title={option.label}>
            {option.label}
          </div>
        ),
      })),
    [locationOptionList]
  );

  /** 停靠点搜索防抖函数：跨渲染稳定，避免每次输入都重新创建 debounce 实例 */
  const debouncedStopSearch = useMemo(
    () =>
      debounce((value: string) => {
        searchCallBack({
          searchType: PositionSelectType.stop,
          stopValue: value,
        });
      }, 1000),
    [searchCallBack]
  );

  /** 位置 Select 清空时重置搜索条件 */
  const handleLocationSelectChange = useCallback(
    (value: any) => {
      if (!value) {
        searchCallBack({
          searchType: PositionSelectType.position,
          value: null,
        });
      }
    },
    [searchCallBack]
  );

  /** 停靠点 Select 聚焦时清空当前值 */
  const handleStopSelectFocus = useCallback(() => {
    searchCallBack({
      searchType: PositionSelectType.stop,
      stopValue: null,
    });
  }, [searchCallBack]);

  /**
   * 车辆选择后主动释放 Select 焦点/文本选择状态。
   * 否则从下拉项选中后鼠标移出面板顶部时，浏览器可能保留拖拽选择状态，
   * 触发右侧可滚动面板的原生自动滚动。
   */
  const handleVehicleSelect = useCallback(
    (value: any) => {
      handleSelectStop(value);
      vehicleSelectRef.current?.blur?.();
      window.getSelection()?.removeAllRanges();
    },
    [handleSelectStop]
  );

  /** 经纬度表单提交 */
  const handleLatLngFinish = useCallback(
    (values: any) => {
      handleSelectlatlng(values);
    },
    [handleSelectlatlng]
  );

  return (
    <>
      <div className="search-tool-container">
        {/* 地图加载窗口期：整条约 loading 覆盖层盖住全部控件，控件同时处于禁用态（AGENTS.md 坑17） */}
        {!isMapReady && (
          <div className="search-tool-loading">
            <Spin size="small" />
            <span>加载中...</span>
          </div>
        )}
        <div className="search-detail">
          <Select
            defaultValue={searchForm.searchType}
            style={{ width: 120 }}
            onChange={handleChange}
            options={selectOptions}
            disabled={controlsDisabled}
            getPopupContainer={getLocalPopupContainer}
            popupClassName="map-editor-search-type-dropdown"
          />
          {searchForm.searchType === 'position' && (
            <div className="select-container">
              <Select
                ref={locationSelectRef}
                allowClear
                showSearch
                open={isLocationDropdownOpen}
                disabled={controlsDisabled}
                value={searchForm.value}
                showArrow={true}
                filterOption={false}
                notFoundContent={null}
                getPopupContainer={getLocalPopupContainer}
                placeholder="请搜索位置"
                style={{ width: 200, marginLeft: '1px' }}
                dropdownMatchSelectWidth={false}
                popupClassName="map-editor-location-dropdown"
                options={locationSelectOptions}
                onSearch={handleSearch}
                onChange={handleLocationSelectChange}
                onSelect={hanleOnSelect}
                onBlur={handleBlur}
                onFocus={() => handleFocus(locationOptionList.length > 0)}
              />
            </div>
          )}
          {searchForm.searchType === 'station' && (
            <Cascader
              placeholder="请选择城市站点"
              options={cityList}
              style={{ width: 200, marginLeft: '1px' }}
              onChange={changeCascader}
              disabled={controlsDisabled}
              showSearch
              getPopupContainer={getLocalPopupContainer}
            />
          )}

          {searchForm.searchType === 'stop' && (
            <Select
              showSearch
              allowClear
              disabled={controlsDisabled}
              defaultValue={searchForm.stopValue}
              placeholder="请输入停靠点"
              style={{ width: 200, marginLeft: '1px' }}
              options={stopOptionList}
              getPopupContainer={getLocalPopupContainer}
              onFocus={handleStopSelectFocus}
              onSelect={handleSelectStop}
              onSearch={debouncedStopSearch}
              filterOption={false}
            />
          )}
          {searchForm.searchType === 'vehicle' && (
            <Select
              ref={vehicleSelectRef}
              showSearch
              allowClear
              disabled={controlsDisabled}
              defaultValue={searchForm.vehicleValue}
              placeholder="请输入车号"
              style={{ width: 200, marginLeft: '1px' }}
              onSelect={handleVehicleSelect}
              options={vehicleOptionList}
              getPopupContainer={getLocalPopupContainer}
              filterOption={(input, option) => {
                const label: any = option?.label || '';
                return (
                  label.toLowerCase().indexOf(input.toLowerCase()) >= 0 || false
                );
              }}
            />
          )}
          {searchForm.searchType === 'latLag' && (
            <Select
              disabled={true}
              placeholder="请选择"
              style={{ width: 200, marginLeft: '1px' }}
              className={
                searchForm.searchType === 'latLag'
                  ? 'custom-select-disabled'
                  : ''
              }
            />
          )}
          {searchForm.searchType === 'latLag' && (
            <div className="search-latlng-container">
              <div className="latlng-input">
                <Form
                  form={searchForm.latlngForm}
                  onFinish={handleLatLngFinish}
                >
                  <Form.Item
                    label="经度"
                    name="longitude"
                    colon={false}
                    className="hide-required-mark"
                    rules={[
                      {
                        pattern: /^(?!.*\.\d{16,})[^\d]*\d*([^\d]*\d*)*$/,
                        message: '查询精度最高支持小数点后15位',
                      },
                      {
                        required: true,
                        pattern:
                          /^-?(1[0-7]\d(\.\d+)?|180(\.0+)?|[1-9]\d(\.\d+)?|\d(\.\d+)?)$/,
                        message: '请输入正确的经度信息',
                      },
                    ]}
                  >
                    <Input placeholder="请输入" />
                  </Form.Item>
                  <Form.Item
                    label="纬度"
                    name="latitude"
                    colon={false}
                    className="hide-required-mark"
                    rules={[
                      {
                        required: true,
                        pattern: /^-?(90(\.0+)?|[1-8]?\d(\.\d+)?)$/,
                        message: '请输入正确的纬度信息',
                      },
                      {
                        pattern: /^(?!.*\.\d{16,})[^\d]*\d*([^\d]*\d*)*$/,
                        message: '查询精度最高支持小数点后15位',
                      },
                    ]}
                  >
                    <Input placeholder="请输入" />
                  </Form.Item>
                  <Button
                    className="latlng-button"
                    type="primary"
                    htmlType="submit"
                  >
                    查询地图
                  </Button>
                </Form>
              </div>
            </div>
          )}

          {!isDrawing ? (
            <Button
              type="primary"
              className={
                getPermissionById(PermissionButton.SPV_ADD_AREAS)
                  ? 'addmap-btn'
                  : 'add-button-disabled'
              }
              disabled={!isMapReady || (showEditForm && !isDrawing) || planningMode}
              onClick={onClickDraw}
              clstag={clstagStr(CLSTAG.AddAreas)}
            >
              新增动态地图
            </Button>
          ) : (
            <Button
              type="primary"
              className="addmap-btn"
              onClick={cancelPaint}
              clstag={clstagStr(CLSTAG.CancelAddAreas)}
            >
              取消绘制
            </Button>
          )}
          {/* 路径规划入口：无权限不展示；编辑表单打开/绘制中（controlsDisabled）时禁止进入，避免模式冲突 */}
          {getPermissionById(PermissionButton.SPV_ROUTE_PLAN) && (
            <Button
              type="primary"
              className="route-plan-btn"
              disabled={controlsDisabled}
              onClick={onToggleRoutePlan}
            >
              {planningMode ? '退出路径规划模式' : '路径规划'}
            </Button>
          )}
        </div>
        <div className="cover-map">
          {/* LD 地图开关：控制矢量瓦片图层显隐 */}
          <Checkbox
            className="ld-map-checkbox"
            onChange={toggleLdMap}
            checked={ldMapVisible}
            disabled={!isMapReady || showEditForm}
          >
            <span>LD地图</span>
          </Checkbox>
          {LAYER_CHECKBOX_CONFIG.map((item: any) => {
            return (
              <Checkbox
                key={item.value}
                onChange={() => handleCheckbox(item)}
                checked={!unSelectedLayer.includes(item.value)}
                disabled={!isMapReady || showEditForm}
              >
                <span clstag={item.clstag}>{item.label}</span>
              </Checkbox>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default React.memo(SearchTools);
